import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Calendar, Download, TrendingUp, ShoppingCart, Users, Package, CreditCard,
  DollarSign, AlertTriangle, ArrowUpRight, ArrowDownRight, FileText, RefreshCw, Loader2, ArrowLeft
} from 'lucide-react';
import { reportsAPI } from '../../services/api';
import toast from 'react-hot-toast';

type ReportTab = 'sales' | 'services' | 'payments' | 'customers' | 'inventory' | 'expenses';

const TABS: { id: ReportTab; label: string; icon: React.ElementType }[] = [
  { id: 'sales', label: 'Sales', icon: ShoppingCart },
  { id: 'services', label: 'Services', icon: BarChart3 },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'expenses', label: 'Expenses', icon: DollarSign },
];

const formatCurrency = (v: number) => `£${(v || 0).toFixed(2)}`;
const formatNumber = (v: number) => (v || 0).toLocaleString();

const getPresetRange = (preset: string): { start: string; end: string } => {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  switch (preset) {
    case 'today': return { start: fmt(today), end: fmt(today) };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { start: fmt(y), end: fmt(y) };
    }
    case 'last7': {
      const d = new Date(today); d.setDate(d.getDate() - 6);
      return { start: fmt(d), end: fmt(today) };
    }
    case 'last30': {
      const d = new Date(today); d.setDate(d.getDate() - 29);
      return { start: fmt(d), end: fmt(today) };
    }
    case 'thisMonth': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmt(s), end: fmt(today) };
    }
    case 'lastMonth': {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(s), end: fmt(e) };
    }
    case 'thisYear': {
      const s = new Date(today.getFullYear(), 0, 1);
      return { start: fmt(s), end: fmt(today) };
    }
    default: return { start: fmt(today), end: fmt(today) };
  }
};

// Simple bar chart component
const SimpleBarChart: React.FC<{ data: { label: string; value: number }[]; color?: string; prefix?: string }> = ({ data, color = '#4F46E5', prefix = '' }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-gray-500 w-20 truncate text-right" title={d.label}>{d.label}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
            <div
              className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
              style={{ width: `${Math.max((d.value / max) * 100, 2)}%`, backgroundColor: color }}
            >
              {d.value / max > 0.2 && <span className="text-xs text-white font-medium">{prefix}{typeof d.value === 'number' && d.value % 1 !== 0 ? d.value.toFixed(2) : d.value}</span>}
            </div>
          </div>
          {d.value / max <= 0.2 && <span className="text-xs text-gray-600 font-medium">{prefix}{typeof d.value === 'number' && d.value % 1 !== 0 ? d.value.toFixed(2) : d.value}</span>}
        </div>
      ))}
    </div>
  );
};

// Sparkline-like daily chart
const DailyChart: React.FC<{ data: { date: string; value: number }[]; color?: string }> = ({ data, color = '#4F46E5' }) => {
  if (!data.length) return <p className="text-sm text-gray-400 text-center py-8">No data</p>;
  const max = Math.max(...data.map(d => d.value), 1);
  const barW = Math.max(100 / data.length, 2);
  return (
    <div className="flex items-end gap-px h-40 px-1">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ maxWidth: `${barW}%` }}>
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {d.date}: £{d.value.toFixed(2)}
          </div>
          <div
            className="w-full rounded-t transition-all duration-300 min-h-[2px]"
            style={{ height: `${(d.value / max) * 100}%`, backgroundColor: color }}
          />
          {data.length <= 31 && (
            <span className="text-[9px] text-gray-400 mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">
              {new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// Stat card
const StatCard: React.FC<{ label: string; value: string; sub?: string; icon: React.ElementType; color: string }> = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 truncate">{label}</p>
      <p className="text-lg font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  </div>
);

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReportTab>('sales');
  const [startDate, setStartDate] = useState(() => getPresetRange('thisMonth').start);
  const [endDate, setEndDate] = useState(() => getPresetRange('thisMonth').end);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  const fetchReport = useCallback(async (tab?: ReportTab) => {
    const t = tab || activeTab;
    setLoading(true);
    try {
      const params = { startDate, endDate };
      let res;
      switch (t) {
        case 'sales': res = await reportsAPI.getSalesSummary(params); break;
        case 'services': res = await reportsAPI.getServicePerformance(params); break;
        case 'payments': res = await reportsAPI.getPaymentReport(params); break;
        case 'customers': res = await reportsAPI.getCustomerReport(params); break;
        case 'inventory': res = await reportsAPI.getInventoryReport(params); break;
        case 'expenses': res = await reportsAPI.getExpenseReport(params); break;
      }
      setReportData(res?.data || null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to load report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, startDate, endDate]);

  const handleTabChange = (tab: ReportTab) => {
    setActiveTab(tab);
    setReportData(null);
  };

  const applyPreset = (preset: string) => {
    const { start, end } = getPresetRange(preset);
    setStartDate(start);
    setEndDate(end);
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    let csv = '';
    const tab = activeTab;

    if (tab === 'sales' && reportData.dailySales) {
      csv = 'Date,Orders,Revenue\n' + reportData.dailySales.map((d: any) => `${d._id},${d.orders},${d.revenue.toFixed(2)}`).join('\n');
    } else if (tab === 'services' && reportData.serviceStats) {
      csv = 'Service,Code,Category,Qty,Revenue\n' + reportData.serviceStats.map((s: any) => `"${s.serviceName}",${s.serviceCode},${s.category},${s.totalOrdered},${s.totalRevenue.toFixed(2)}`).join('\n');
    } else if (tab === 'payments' && reportData.byMethod) {
      csv = 'Method,Count,Amount,Refunded\n' + reportData.byMethod.map((m: any) => `${m._id},${m.count},${m.totalAmount.toFixed(2)},${(m.totalRefunded || 0).toFixed(2)}`).join('\n');
    } else if (tab === 'customers' && reportData.topCustomers) {
      csv = 'Name,Email,Phone,Orders,Total Spent,Avg Order\n' + reportData.topCustomers.map((c: any) => `"${c.name}",${c.email || ''},${c.phone || ''},${c.totalOrders},${c.totalSpent.toFixed(2)},${c.avgOrderValue.toFixed(2)}`).join('\n');
    } else if (tab === 'inventory' && reportData.stockSummary) {
      csv = 'Category,Items,Stock,Value,Low Stock\n' + reportData.stockSummary.map((s: any) => `${s._id},${s.itemCount},${s.totalStock},${s.totalValue.toFixed(2)},${s.lowStockItems}`).join('\n');
    } else if (tab === 'expenses' && reportData.byCategory) {
      csv = 'Category,Count,Amount\n' + reportData.byCategory.map((c: any) => `${c._id},${c.count},${c.totalAmount.toFixed(2)}`).join('\n');
    }

    if (!csv) { toast.error('No data to export'); return; }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tab}-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin')} className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Reports</h1>
                <p className="text-sm text-gray-500">Analyse your business performance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleExportCSV} disabled={!reportData || loading} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Date range + Presets */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Quick Range</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'last7', label: 'Last 7 Days' },
                  { key: 'last30', label: 'Last 30 Days' },
                  { key: 'thisMonth', label: 'This Month' },
                  { key: 'lastMonth', label: 'Last Month' },
                  { key: 'thisYear', label: 'This Year' },
                ].map(p => (
                  <button key={p.key} onClick={() => applyPreset(p.key)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <button onClick={() => fetchReport()} disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors shadow-sm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Run Report
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${active ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Report Content */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-3" />
            <p className="text-gray-500 font-medium">Generating report...</p>
          </div>
        ) : !reportData ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Select a date range and click "Run Report"</p>
            <p className="text-xs text-gray-400 mt-1">Data will appear here</p>
          </div>
        ) : (
          <div className="space-y-6">
            {activeTab === 'sales' && <SalesReport data={reportData} />}
            {activeTab === 'services' && <ServicesReport data={reportData} />}
            {activeTab === 'payments' && <PaymentsReport data={reportData} />}
            {activeTab === 'customers' && <CustomersReport data={reportData} />}
            {activeTab === 'inventory' && <InventoryReport data={reportData} />}
            {activeTab === 'expenses' && <ExpensesReport data={reportData} />}
          </div>
        )}
      </div>
    </div>
  );
};

/* =========================================
   SALES REPORT
   ========================================= */
const SalesReport: React.FC<{ data: any }> = ({ data }) => {
  const s = data.summary || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Orders" value={formatNumber(s.totalOrders)} icon={ShoppingCart} color="bg-blue-100 text-blue-600" />
        <StatCard label="Revenue" value={formatCurrency(s.totalRevenue)} icon={TrendingUp} color="bg-green-100 text-green-600" />
        <StatCard label="Avg Order Value" value={formatCurrency(s.avgOrderValue)} icon={ArrowUpRight} color="bg-indigo-100 text-indigo-600" />
        <StatCard label="Outstanding" value={formatCurrency(s.totalOutstanding)} icon={ArrowDownRight} color="bg-red-100 text-red-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Subtotal" value={formatCurrency(s.totalSubtotal)} icon={DollarSign} color="bg-gray-100 text-gray-600" />
        <StatCard label="VAT Collected" value={formatCurrency(s.totalVAT)} icon={DollarSign} color="bg-amber-100 text-amber-600" />
        <StatCard label="Discounts Given" value={formatCurrency(s.totalDiscounts)} icon={DollarSign} color="bg-purple-100 text-purple-600" />
        <StatCard label="Total Items" value={formatNumber(s.totalItems)} icon={Package} color="bg-teal-100 text-teal-600" />
      </div>

      {(s.loyaltyPointsEarned > 0 || s.loyaltyPointsRedeemed > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="Loyalty Earned" value={`${formatNumber(s.loyaltyPointsEarned)} pts`} icon={TrendingUp} color="bg-yellow-100 text-yellow-700" />
          <StatCard label="Loyalty Redeemed" value={`${formatNumber(s.loyaltyPointsRedeemed)} pts`} icon={ArrowDownRight} color="bg-yellow-100 text-yellow-700" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Orders by Status</h3>
          {data.ordersByStatus?.length ? (
            <SimpleBarChart data={data.ordersByStatus.map((s: any) => ({ label: (s._id || 'unknown').replace('_', ' '), value: s.count }))} color="#6366F1" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Orders by Payment Status</h3>
          {data.ordersByPaymentStatus?.length ? (
            <SimpleBarChart data={data.ordersByPaymentStatus.map((s: any) => ({ label: (s._id || 'unknown').replace('_', ' '), value: s.count }))} color="#22C55E" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Daily Sales Trend</h3>
        {data.dailySales?.length ? (
          <DailyChart data={data.dailySales.map((d: any) => ({ date: d._id, value: d.revenue }))} color="#4F46E5" />
        ) : <p className="text-sm text-gray-400">No data</p>}
      </div>
    </>
  );
};

/* =========================================
   SERVICES REPORT
   ========================================= */
const ServicesReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Revenue by Category</h3>
      {data.categoryStats?.length ? (
        <SimpleBarChart data={data.categoryStats.map((c: any) => ({ label: c._id, value: c.totalRevenue }))} color="#8B5CF6" prefix="£" />
      ) : <p className="text-sm text-gray-400">No data</p>}
    </div>

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Service Performance</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Service</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Qty Sold</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Revenue</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">VAT</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Avg Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(data.serviceStats || []).map((s: any, i: number) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-3 font-medium text-gray-900">{s.serviceName}<span className="text-xs text-gray-400 ml-1">{s.serviceCode}</span></td>
                <td className="px-5 py-3 text-gray-600 capitalize">{s.category}</td>
                <td className="px-5 py-3 text-right text-gray-900">{s.totalOrdered}</td>
                <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatCurrency(s.totalRevenue)}</td>
                <td className="px-5 py-3 text-right text-gray-500">{formatCurrency(s.totalVAT)}</td>
                <td className="px-5 py-3 text-right text-gray-500">{formatCurrency(s.avgPrice)}</td>
              </tr>
            ))}
            {!data.serviceStats?.length && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">No service data for this period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </>
);

/* =========================================
   PAYMENTS REPORT
   ========================================= */
const PaymentsReport: React.FC<{ data: any }> = ({ data }) => {
  const s = data.summary || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Payments" value={formatNumber(s.totalPayments)} icon={CreditCard} color="bg-blue-100 text-blue-600" />
        <StatCard label="Total Collected" value={formatCurrency(s.totalCollected)} icon={TrendingUp} color="bg-green-100 text-green-600" />
        <StatCard label="Total Refunded" value={formatCurrency(s.totalRefunded)} icon={ArrowDownRight} color="bg-red-100 text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">By Payment Method</h3>
          {data.byMethod?.length ? (
            <SimpleBarChart data={data.byMethod.map((m: any) => ({ label: (m._id || 'unknown').replace('_', ' '), value: m.totalAmount }))} color="#0EA5E9" prefix="£" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Daily Payment Trend</h3>
          {data.dailyPayments?.length ? (
            <DailyChart data={data.dailyPayments.map((d: any) => ({ date: d._id, value: d.total }))} color="#22C55E" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Payment Method Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Count</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Refunded</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.byMethod || []).map((m: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900 capitalize">{(m._id || 'unknown').replace('_', ' ')}</td>
                  <td className="px-5 py-3 text-right text-gray-900">{m.count}</td>
                  <td className="px-5 py-3 text-right font-semibold text-green-600">{formatCurrency(m.totalAmount)}</td>
                  <td className="px-5 py-3 text-right text-red-500">{formatCurrency(m.totalRefunded || 0)}</td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatCurrency(m.totalAmount - (m.totalRefunded || 0))}</td>
                </tr>
              ))}
              {!data.byMethod?.length && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No payment data for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* =========================================
   CUSTOMERS REPORT
   ========================================= */
const CustomersReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <StatCard label="New Customers" value={formatNumber(data.newCustomers)} icon={Users} color="bg-indigo-100 text-indigo-600" />
      <StatCard label="Active Customers" value={formatNumber(data.activeCustomers)} sub="placed orders in period" icon={Users} color="bg-green-100 text-green-600" />
    </div>

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Top 20 Customers by Spend</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Contact</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Orders</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Total Spent</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Avg Order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(data.topCustomers || []).map((c: any, i: number) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-3 text-gray-400 font-mono">{i + 1}</td>
                <td className="px-5 py-3 font-medium text-gray-900">{c.name?.trim() || 'Unknown'}</td>
                <td className="px-5 py-3 text-gray-500 text-xs">{c.email || c.phone || '—'}</td>
                <td className="px-5 py-3 text-right text-gray-900">{c.totalOrders}</td>
                <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatCurrency(c.totalSpent)}</td>
                <td className="px-5 py-3 text-right text-gray-500">{formatCurrency(c.avgOrderValue)}</td>
              </tr>
            ))}
            {!data.topCustomers?.length && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">No customer data for this period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </>
);

/* =========================================
   INVENTORY REPORT
   ========================================= */
const InventoryReport: React.FC<{ data: any }> = ({ data }) => {
  const s = data.summary || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Items" value={formatNumber(s.totalItems)} icon={Package} color="bg-blue-100 text-blue-600" />
        <StatCard label="Total Units" value={formatNumber(s.totalUnits)} icon={Package} color="bg-teal-100 text-teal-600" />
        <StatCard label="Inventory Value" value={formatCurrency(s.totalValue)} icon={DollarSign} color="bg-green-100 text-green-600" />
        <StatCard label="Low Stock Items" value={formatNumber(s.lowStockCount)} icon={AlertTriangle} color="bg-red-100 text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Stock by Category</h3>
          {data.stockSummary?.length ? (
            <SimpleBarChart data={data.stockSummary.map((s: any) => ({ label: (s._id || 'Other').replace('_', ' '), value: s.totalValue }))} color="#14B8A6" prefix="£" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Transaction Summary</h3>
          {data.transactionSummary?.length ? (
            <SimpleBarChart data={data.transactionSummary.map((t: any) => ({ label: (t._id || 'unknown').replace('_', ' '), value: t.count }))} color="#F59E0B" />
          ) : <p className="text-sm text-gray-400">Run report with a date range to see transactions</p>}
        </div>
      </div>

      {data.lowStockItems?.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-red-100 bg-red-50 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <h3 className="text-sm font-semibold text-red-800">Low Stock Alerts</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Item</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Current</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Minimum</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Reorder Level</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.lowStockItems.map((item: any, i: number) => (
                  <tr key={i} className="hover:bg-red-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{item.name} <span className="text-xs text-gray-400">[{item.code}]</span></td>
                    <td className="px-5 py-3 text-gray-600 capitalize">{(item.category || '').replace('_', ' ')}</td>
                    <td className="px-5 py-3 text-right font-bold text-red-600">{item.currentStock} {item.unit}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{item.minimumStock} {item.unit}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{item.reorderLevel} {item.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Stock Summary by Category</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Total Stock</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Value</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Low Stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.stockSummary || []).map((s: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900 capitalize">{(s._id || 'Other').replace('_', ' ')}</td>
                  <td className="px-5 py-3 text-right text-gray-900">{s.itemCount}</td>
                  <td className="px-5 py-3 text-right text-gray-900">{formatNumber(s.totalStock)}</td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatCurrency(s.totalValue)}</td>
                  <td className="px-5 py-3 text-right">{s.lowStockItems > 0 ? <span className="text-red-600 font-semibold">{s.lowStockItems}</span> : <span className="text-green-600">0</span>}</td>
                </tr>
              ))}
              {!data.stockSummary?.length && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No inventory data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

/* =========================================
   EXPENSES REPORT
   ========================================= */
const ExpensesReport: React.FC<{ data: any }> = ({ data }) => {
  const s = data.summary || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Expenses" value={formatNumber(s.totalExpenses)} icon={CreditCard} color="bg-blue-100 text-blue-600" />
        <StatCard label="Total Amount" value={formatCurrency(s.totalAmount)} icon={DollarSign} color="bg-red-100 text-red-600" />
        <StatCard label="Avg Expense" value={formatCurrency(s.avgExpense)} icon={ArrowUpRight} color="bg-amber-100 text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">By Category</h3>
          {data.byCategory?.length ? (
            <SimpleBarChart data={data.byCategory.map((c: any) => ({ label: c._id, value: c.totalAmount }))} color="#EF4444" prefix="£" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Daily Expenses Trend</h3>
          {data.dailyExpenses?.length ? (
            <DailyChart data={data.dailyExpenses.map((d: any) => ({ date: d._id, value: d.total }))} color="#EF4444" />
          ) : <p className="text-sm text-gray-400">No data</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Expense Category Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Count</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">% of Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.byCategory || []).map((c: any, i: number) => {
                const pct = s.totalAmount > 0 ? ((c.totalAmount / s.totalAmount) * 100).toFixed(1) : '0';
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{c._id}</td>
                    <td className="px-5 py-3 text-right text-gray-900">{c.count}</td>
                    <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatCurrency(c.totalAmount)}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{pct}%</td>
                  </tr>
                );
              })}
              {!data.byCategory?.length && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-gray-400">No expense data for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Reports;
